import { Field, Float, ID, InputType, ObjectType } from 'type-graphql'
import { modelOptions, Prop } from '@typegoose/typegoose'
import { BaseEntity } from '../../base/BaseEntity'
import { generateEntityId } from '../../generateEntityId'
import { CreditPurchaseStatusEnum } from './CreditPurchaseStatusEnum'
import { PaymentGatewayProviderEnum } from '../../payment/PaymentGatewayProviderEnum'
import type { PaymentGatewayProvider } from '../../payment/PaymentGatewayProviderEnum'

/**
 * A single attempt to buy credits — the first-class record a checkout session and its
 * settlement webhook both act on. `creditPackageId` is provenance only; `credits`,
 * `unitPrice` and `currency` are frozen snapshots taken at creation so repricing or
 * retiring the source `CreditPackage` later never rewrites what a past buyer paid or
 * received.
 */
@ObjectType()
@InputType('CreditPurchaseInput')
@modelOptions({ schemaOptions: { timestamps: true, collection: 'aicreditpurchases' } })
export class CreditPurchase extends BaseEntity {
  @Field(() => ID)
  @Prop({ type: String, required: true, default: () => generateEntityId('crpu') })
  declare _id: string

  /** Whose `CreditAccount.balance` this purchase credits once settled. */
  @Field(() => String)
  @Prop({ type: String, required: true })
  ownerId: string

  /** Provenance only — never re-read for pricing; see the snapshot fields below. */
  @Field(() => ID)
  @Prop({ type: String, required: true })
  creditPackageId: string

  /** Snapshot of `CreditPackage.credits` at purchase time — frozen so repricing the package never rewrites this record. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  credits: number

  /** Snapshot of `CreditPackage.price` at purchase time — frozen so repricing the package never rewrites this record. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  unitPrice: number

  /** Amount actually payable after `discountAmount` is applied. */
  @Field(() => Float)
  @Prop({ type: Number, required: true })
  amount: number

  @Field(() => Float)
  @Prop({ type: Number, required: true, default: 0 })
  discountAmount: number

  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  couponCode?: string

  /**
   * Resolved `Coupon._id` at purchase time — what `RedeemCoupon` claims by at settlement, never
   * `couponCode` (a raw, case-sensitive client string that can drift from the coupon's
   * normalized code, and that a rename between purchase and settlement would orphan).
   */
  @Field(() => ID, { nullable: true })
  @Prop({ type: String })
  couponId?: string

  /** Snapshot of `CreditPackage.currency` at purchase time — frozen so repricing the package never rewrites this record. */
  @Field(() => String)
  @Prop({ type: String, required: true })
  currency: string

  @Field(() => CreditPurchaseStatusEnum)
  @Prop({ type: String, enum: CreditPurchaseStatusEnum, required: true })
  declare status: CreditPurchaseStatusEnum

  /** Absent for a fully-discounted purchase, which settles without ever calling a gateway. */
  @Field(() => PaymentGatewayProviderEnum, { nullable: true })
  @Prop({ type: String, enum: PaymentGatewayProviderEnum })
  gatewayProvider?: PaymentGatewayProvider

  /** Set at settlement, from the gateway's paid-event payload. */
  @Field(() => String, { nullable: true })
  @Prop({ type: String })
  gatewayPaymentId?: string

  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  paidAt?: Date

  /**
   * Set only after `PurchaseCredits` succeeds — nullable so a crash between the Pending->Paid
   * claim and the credit grant leaves a purchase that is Paid but still unset here. The claim's
   * filter treats that state as re-claimable, so the gateway's own webhook retry recovers it
   * instead of matching nothing and silently no-op'ing forever.
   */
  @Field(() => Date, { nullable: true })
  @Prop({ type: Date })
  creditsGrantedAt?: Date
}
